
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Manutencao from "./pages/Manutencao";
import { WalkthroughProvider } from "./components/Walkthrough/WalkthroughProvider";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/Layout/AppLayout";
import Index from "./pages/Index";
import Home from "./pages/Home";
import Diamantes from "./pages/Diamantes";
import LojaRecompensas from "./pages/LojaRecompensas";
import ExtratosDiamantes from "./pages/ExtratosDiamantes";
import RankingVendas from "./pages/RankingVendas";
import NovaVenda from "./pages/NovaVenda";
import MinhasVendas from "./pages/MinhasVendas";
import Garantias from "./pages/Garantias";
import RecibosMultinivel from "./pages/RecibosMultinivel";
import MinhaRede from "./pages/MinhaRede";
import LinksIndicacao from "./pages/LinksIndicacao";
import ExtratoMensal from "./pages/ExtratoMensal";
import Pedidos from "./pages/Pedidos";
import Suporte from "./pages/Suporte";
import SideLogin from "./pages/SideLogin";
import SideRegister from "./pages/SideRegister";
import CadastroCliente from "./pages/CadastroCliente";
import HomeCliente from "./pages/HomeCliente";
import MeusCupons from "./pages/MeusCupons";
import AdminCupons from "./pages/AdminCupons";
import PreCadastros from "./pages/PreCadastros";
import PreCadastrosSettings from "./pages/PreCadastrosSettings";
import PreCadastroDetail from "./pages/PreCadastroDetail";
import Users from "./pages/Users";
import UserDetail from "./pages/UserDetail";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import FormularioPage from "./pages/FormularioPage";
import AdminPushNotifications from "./pages/AdminPushNotifications";
import AdminAllNotifications from "./pages/AdminAllNotifications";
import RelatoriosVendas from "./pages/RelatoriosVendas";
import RelatorioVendaDetalhe from "./pages/RelatorioVendaDetalhe";
import RelatoriosResgates from "./pages/RelatoriosResgates";
import ResgateItemDetalhe from "./pages/ResgateItemDetalhe";
import GerenciamentoItens from "./pages/GerenciamentoItens";
import GerenciamentoItemEdit from "./pages/GerenciamentoItemEdit";
import PrimeiroAcesso from "./pages/PrimeiroAcesso";

const queryClient = new QueryClient();

// Separa as rotas principais para permitir o switch para página de manutenção
const AllRoutes = () => (
  <Routes>
    {/* Rotas de autenticação (públicas) */}
    <Route path="authentication/side-login" element={<SideLogin />} />
    <Route path="authentication/side-register" element={<SideRegister />} />
                
    {/* Rota de cadastro de cliente (pública) */}
    <Route path="cadastro-cliente" element={<CadastroCliente />} />
    <Route path="cadastro-cliente/:indicador" element={<CadastroCliente />} />
                
    {/* Rotas protegidas */}
    <Route 
      path="/" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><Index /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/home" 
      element={
        <ProtectedRoute requiredRoles={['revendedora', 'coordenadora']}>
          <AppLayout><Home /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/home-cliente" 
      element={
        <ProtectedRoute requiredRoles={['cliente']}>
          <AppLayout><HomeCliente /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/diamantes" 
      element={
        <ProtectedRoute>
          <AppLayout><Diamantes /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/loja-recompensas" 
      element={
        <ProtectedRoute>
          <AppLayout><LojaRecompensas /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/extratos-diamantes" 
      element={
        <ProtectedRoute requiredRoles={['revendedora', 'coordenadora', 'cliente', 'admin']}>
          <AppLayout><ExtratosDiamantes /></AppLayout>
        </ProtectedRoute>
      } 
    />                     
    <Route 
      path="/ranking-vendas" 
      element={
        <ProtectedRoute>
          <AppLayout><RankingVendas /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/nova-venda" 
      element={
        <ProtectedRoute>
          <AppLayout><NovaVenda /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/minhas-vendas" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'revendedora', 'coordenadora']}>
          <AppLayout><MinhasVendas /></AppLayout>
        </ProtectedRoute>
      } 
    />                
    <Route 
      path="/garantias" 
      element={
        <ProtectedRoute>
          <AppLayout><Garantias /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/recibos-multinivel" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><RecibosMultinivel /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/pedidos" 
      element={
        <ProtectedRoute requiredRoles={['cliente']}>
          <AppLayout><Pedidos /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/suporte" 
      element={
        <ProtectedRoute>
          <AppLayout><Suporte /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/minha-rede" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'coordenadora', 'revendedora', 'cliente']}>
          <AppLayout><MinhaRede /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/links-indicacao" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'coordenadora', 'revendedora', 'cliente']}>
          <LinksIndicacao />
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/extrato-mensal" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'revendedora', 'coordenadora', 'cliente']}>
          <AppLayout><ExtratoMensal /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/meus-cupons" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><MeusCupons /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/admin-cupons" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><AdminCupons /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/admin-push-notifications" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><AdminPushNotifications /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/admin-all-notifications" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><AdminAllNotifications /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/usuarios" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><Users /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/usuarios/:id" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><UserDetail /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/pre-cadastros" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'coordenadora']}>
          <AppLayout><PreCadastros /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/pre-cadastros/:id" 
      element={
        <ProtectedRoute requiredRoles={['admin', 'coordenadora']}>
          <AppLayout><PreCadastroDetail /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/revender" 
      element={
        <FormularioPage />
      } 
    />
    <Route 
      path="/revender/:indicatedCode" 
      element={
        <FormularioPage />
      } 
    />
    <Route 
      path="/pre-cadastros/settings" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><PreCadastrosSettings /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/relatorios-vendas" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><RelatoriosVendas /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/relatorios-vendas/:id" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><RelatorioVendaDetalhe /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/relatorios-resgates" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><RelatoriosResgates /></AppLayout>
        </ProtectedRoute>
      } 
    />
    <Route 
      path="/relatorios-resgates/item/:id" 
      element={
        <ProtectedRoute requiredRoles={['admin']}>
          <AppLayout><ResgateItemDetalhe /></AppLayout>
        </ProtectedRoute>
      } 
    />
                
    <Route
      path="/gerenciamento-itens"
      element={
        <ProtectedRoute requiredRoles={['admin', 'matriz']}>
          <AppLayout><GerenciamentoItens /></AppLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/gerenciamento-itens/:id"
      element={
        <ProtectedRoute requiredRoles={['admin', 'matriz']}>
          <AppLayout><GerenciamentoItemEdit /></AppLayout>
        </ProtectedRoute>
      }
    />

    <Route
      path="/primeiro-acesso/:id"
      element={
        <ProtectedRoute requiredRoles={['revendedora', 'pendente']}>
          <AppLayout><PrimeiroAcesso /></AppLayout>
        </ProtectedRoute>
      } 
    />
    
    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const RoutesOrMaintenance = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const bypass = params.get('bypass') === 'true';
  if (true) return <AllRoutes />;
  return (
    <Routes>
      <Route path="*" element={<Manutencao />} />
    </Routes>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <WalkthroughProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename="/v2">
              <RoutesOrMaintenance />
            </BrowserRouter>
          </WalkthroughProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
